import { useEffect, useState } from 'react';
import type { UpdateCpqSettingsInput } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { DataState } from '../../components/ui/DataState.js';
import { TextField } from '../../components/ui/Field.js';
import { useBackfillOpportunityCpqQuotes } from '../opportunities/api.js';
import { LegacyImportCard } from './LegacyImportCard.js';
import { useCpqQuotes, useCpqSettings, useTestCpqConnection, useUpdateCpqSettings } from './api.js';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatTotal(value: string | null): string {
  if (value == null || value === '') return '—';
  const n = Number(value);
  return Number.isFinite(n) ? currency.format(n) : value;
}

export function CpqIntegrationPage(): JSX.Element {
  const settingsQuery = useCpqSettings();
  const updateMutation = useUpdateCpqSettings();
  const testMutation = useTestCpqConnection();
  const settings = settingsQuery.data;
  const quotesQuery = useCpqQuotes(Boolean(settings?.configured));

  const [baseUrl, setBaseUrl] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [accessKey, setAccessKey] = useState('');
  const [publicKey, setPublicKey] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [clearPrivateKey, setClearPrivateKey] = useState(false);
  const [launchTemplate, setLaunchTemplate] = useState('');
  const [saved, setSaved] = useState(false);

  const backfillMutation = useBackfillOpportunityCpqQuotes();

  useEffect(() => {
    if (!settings) return;
    setEnabled(settings.enabled);
    setBaseUrl(settings.baseUrl ?? '');
    setAccessKey(settings.accessKey ?? '');
    setPublicKey(settings.publicKey ?? '');
    setPrivateKey('');
    setClearPrivateKey(false);
    setLaunchTemplate(settings.quoteLaunchUrlTemplate ?? '');
  }, [settings]);

  const handleSave = (): void => {
    setSaved(false);
    const input: UpdateCpqSettingsInput = {
      enabled,
      baseUrl: baseUrl.trim() || null,
      accessKey: accessKey.trim() || null,
      publicKey: publicKey.trim() || null,
      quoteLaunchUrlTemplate: launchTemplate.trim() || null,
    };
    if (clearPrivateKey) {
      input.privateKey = null;
    } else if (privateKey.trim()) {
      input.privateKey = privateKey.trim();
    }
    updateMutation.mutate(input, {
      onSuccess: () => {
        setSaved(true);
        setPrivateKey('');
        setClearPrivateKey(false);
      },
    });
  };

  const saveError =
    updateMutation.error instanceof ApiError
      ? updateMutation.error.message
      : updateMutation.error
        ? 'Failed to save ConnectWise CPQ settings.'
        : null;

  const testResult = testMutation.data;
  const quotes = quotesQuery.data?.quotes ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="ConnectWise CPQ"
        description="Read-only connection to ConnectWise Sell (CPQ). Link a quote to an opportunity and copy its products."
      />

      <Card>
        <DataState
          isLoading={settingsQuery.isLoading}
          isError={settingsQuery.isError}
          error={settingsQuery.error}
          onRetry={() => void settingsQuery.refetch()}
          isEmpty={false}
        >
          {settings ? (
            <div className="max-w-2xl space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={settings.enabled ? 'green' : 'gray'}>
                  {settings.enabled ? 'Integration on' : 'Integration off'}
                </Badge>
                <Badge tone={settings.configured ? 'green' : 'amber'}>
                  {settings.configured ? 'Configured' : 'Not configured'}
                </Badge>
                {settings.managedInDatabase ? <Badge tone="blue">Managed in app</Badge> : null}
                {!settings.canStoreSecrets ? (
                  <span className="text-xs text-amber-600">
                    No encryption key on the server — the private key cannot be stored from the UI.
                  </span>
                ) : null}
              </div>

              <label className="flex items-start gap-2 rounded-lg bg-slate-50 p-3 text-sm">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                />
                <span>
                  <span className="font-medium text-slate-700">
                    Enable the ConnectWise CPQ integration
                  </span>
                  <span className="mt-0.5 block text-xs text-slate-500">
                    When off, the CPQ tab is hidden on every opportunity and quote linking and
                    product sync are disabled. Credentials are kept, so you can switch it back on
                    at any time. Save to apply.
                  </span>
                </span>
              </label>

              <p className="text-sm text-slate-500">
                The ConnectWise Sell API uses Basic authentication with an API key pair. The access
                key is found in the URL parameters when you are signed in to Sell; the public and
                private keys come from a ConnectWise API key. A value saved here is stored encrypted
                and overrides the matching environment variable.
              </p>

              <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                  label="Base URL"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder="https://sellapi.quosalsell.com"
                />
                <TextField
                  label="Access key"
                  value={accessKey}
                  onChange={(e) => setAccessKey(e.target.value)}
                  placeholder="Sell access key"
                />
                <TextField
                  label="Public key"
                  value={publicKey}
                  onChange={(e) => setPublicKey(e.target.value)}
                  placeholder="API public key"
                />
                <div>
                  <TextField
                    label="Private key"
                    type="password"
                    value={privateKey}
                    onChange={(e) => {
                      setPrivateKey(e.target.value);
                      if (e.target.value) setClearPrivateKey(false);
                    }}
                    disabled={clearPrivateKey || !settings.canStoreSecrets}
                    placeholder={
                      settings.hasPrivateKey ? '•••••••• (stored)' : 'Enter the private key'
                    }
                  />
                  {settings.hasPrivateKey ? (
                    <label className="mt-1.5 flex items-center gap-2 text-xs text-slate-500">
                      <input
                        type="checkbox"
                        checked={clearPrivateKey}
                        onChange={(e) => {
                          setClearPrivateKey(e.target.checked);
                          if (e.target.checked) setPrivateKey('');
                        }}
                      />
                      Clear the stored private key (fall back to the environment value)
                    </label>
                  ) : null}
                </div>
              </div>

              <div>
                <TextField
                  label="Open-quote URL template"
                  value={launchTemplate}
                  onChange={(e) => setLaunchTemplate(e.target.value)}
                  placeholder="https://<tenant>.quosalsell.com/QuosalWeb/quote.dashboard?accesskey=...&idquotemain={idquotemain}&submodules=quote.home"
                />
                <p className="mt-1 text-xs text-slate-500">
                  Used by the “Open quote” button on an opportunity. The{' '}
                  <code>{'{idquotemain}'}</code> placeholder is replaced with the quote’s CPQ id.
                  Leave blank to use the default, which opens the quote directly in ConnectWise CPQ
                  (Quosal). ConnectWise Manage (na.myconnectwise.net) is not used.
                </p>
              </div>

              {saveError ? <p className="text-sm text-rose-600">{saveError}</p> : null}
              {saved ? <p className="text-sm text-emerald-600">Settings saved.</p> : null}
              {testResult ? (
                <p className={`text-sm ${testResult.ok ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {testResult.message}
                  {testResult.ok && testResult.sampleCount != null
                    ? ` (${testResult.sampleCount} quote${testResult.sampleCount === 1 ? '' : 's'} reachable)`
                    : ''}
                </p>
              ) : null}

              <div className="flex items-center gap-2">
                <Button onClick={handleSave} isLoading={updateMutation.isPending}>
                  Save settings
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => testMutation.mutate()}
                  isLoading={testMutation.isPending}
                  disabled={!settings.configured}
                >
                  Test connection
                </Button>
              </div>
            </div>
          ) : null}
        </DataState>
      </Card>

      {settings?.configured ? (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-800">Link opportunities to quotes</h2>
              <p className="text-xs text-slate-500">
                Match every ConnectWise-synced opportunity to its CPQ quote (by ConnectWise
                opportunity id). Native opportunities are linked individually from the opportunity.
              </p>
            </div>
            <Button
              variant="secondary"
              onClick={() => backfillMutation.mutate()}
              isLoading={backfillMutation.isPending}
            >
              Backfill quote links
            </Button>
          </div>
          {backfillMutation.data ? (
            <p className="mt-3 text-sm text-emerald-600">
              Scanned {backfillMutation.data.scanned} opportunities · matched{' '}
              {backfillMutation.data.matched} · linked {backfillMutation.data.linked}.
            </p>
          ) : null}
          {backfillMutation.error ? (
            <p className="mt-3 text-sm text-rose-600">
              {backfillMutation.error instanceof ApiError
                ? backfillMutation.error.message
                : 'Failed to backfill quote links.'}
            </p>
          ) : null}
        </Card>
      ) : null}

      {settings?.configured ? <LegacyImportCard /> : null}

      {settings?.configured ? (
        <Card>
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-slate-800">Recent quotes</h2>
              <p className="text-xs text-slate-500">
                Latest quotes from ConnectWise Sell (read-only preview).
              </p>
            </div>
            <Button
              variant="secondary"
              onClick={() => void quotesQuery.refetch()}
              isLoading={quotesQuery.isFetching}
            >
              Refresh
            </Button>
          </div>
          <DataState
            isLoading={quotesQuery.isLoading}
            isError={quotesQuery.isError}
            error={quotesQuery.error}
            onRetry={() => void quotesQuery.refetch()}
            isEmpty={!quotesQuery.isLoading && quotes.length === 0}
            emptyTitle="No quotes found"
            emptyDescription="No quotes were returned from ConnectWise Sell."
          >
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Quote #</th>
                    <th className="px-3 py-2 font-medium">Name</th>
                    <th className="px-3 py-2 font-medium">Account</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 text-right font-medium">Total</th>
                    <th className="px-3 py-2 text-right font-medium">Recurring</th>
                    <th className="px-3 py-2 font-medium">Modified</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {quotes.map((quote) => (
                    <tr key={quote.id}>
                      <td className="px-3 py-2 tabular-nums text-slate-600">
                        {quote.quoteNumber ?? '—'}
                        {quote.quoteVersion != null ? `.${quote.quoteVersion}` : ''}
                      </td>
                      <td className="px-3 py-2 text-slate-800">{quote.name ?? '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{quote.accountName ?? '—'}</td>
                      <td className="px-3 py-2">
                        {quote.isAccepted ? (
                          <Badge tone="green">Won</Badge>
                        ) : quote.isLost ? (
                          <Badge tone="red">Lost</Badge>
                        ) : (
                          <Badge tone="gray">{quote.status ?? 'Open'}</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-800">
                        {formatTotal(quote.total)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                        {formatTotal(quote.recurringTotal)}
                      </td>
                      <td className="px-3 py-2 text-slate-500">
                        {formatDateTime(quote.modifyDate)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DataState>
        </Card>
      ) : null}
    </div>
  );
}
